import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Grid, 
  IconButton, 
  Divider,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Palette as AppearanceIcon,
  Print as PrinterIcon,
  Payments as PaymentIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Receipt as BillingIcon
} from '@mui/icons-material';
import DashboardContext from '../../../contexts/DashboardContext';
import DashboardButton from '../../../common/DashboardButton';
import DashboardCard from '../../../common/DashboardCard';
import { getSettings, updateSettings } from '../../../services/settingService.js';
import { changeLanguage } from '../../../../../i18n';

// Import dialog components
import AppearanceDialog from './dialogs/AppearanceDialog';
import PrinterDialog from './dialogs/PrinterDialog';
import PaymentDialog from './dialogs/PaymentDialog';
import RolePermissionsDialog from './dialogs/RolePermissionsDialog';
import BillingDialog from './dialogs/BillingDialog';
import NotificationSettingsDialog from './dialogs/NotificationSettingsDialog';
import SettingsSection from './SettingsSection';

const Settings = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { 
    isDarkMode, 
    toggleThemeMode, 
    userInfo
  } = useContext(DashboardContext);
  
  // For framer-motion components
  const MotionDiv = motion.div;
  
  // Permission and user state
  const [userPermissions, setUserPermissions] = useState([]);
  const [userData, setUserData] = useState(null);
  
  // Dialog states
  const [openAppearanceDialog, setOpenAppearanceDialog] = useState(false);
  const [openPrinterDialog, setOpenPrinterDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [openBillingDialog, setOpenBillingDialog] = useState(false);
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false); // NEW
  
  // Current settings states
  const [currentLanguage, setCurrentLanguage] = useState('English');
  const [printerStatus, setPrinterStatus] = useState('Not configured');
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Billing info state
  const [billingInfo, setBillingInfo] = useState({
    companyName: '',
    businessAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    taxId: '',
    vatNumber: '',
    billingContact: {
      name: '',
      email: '',
      phone: ''
    },
    paymentMethods: [],
    subscription: {
      plan: 'basic',
      status: 'active',
      nextBilling: '',
      amount: 29
    }
  });
  
  // Initialize user data and permissions
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        setUserData(tokenData);
        
        if (tokenData.permissions) {
          setUserPermissions(tokenData.permissions);
        } else {
          const storedPermissions = localStorage.getItem('userPermissions');
          if (storedPermissions) {
            try {
              const parsedPermissions = JSON.parse(storedPermissions);
              setUserPermissions(parsedPermissions);
            } catch (error) {
              console.error('Error parsing permissions', error);
              setUserPermissions([]);
            }
          }
        }
      } catch (error) {
        console.error('Error processing token', error);
      }
    }
  }, []);
  
  // Permission checker function
  const hasPermission = (permissionUrl) => {
    // Admin has access to everything
    const isAdmin = userData?.role === 'Admin' || userInfo?.role === 'Admin';
    if (isAdmin) {
      return true;
    }
    
    // Check if user has specific permission
    return userPermissions.some(p => p.url === permissionUrl);
  };
  
  // Load from localStorage (fallback function)
  const loadFromLocalStorage = () => {
    try {
      const storedSettings = localStorage.getItem('posSystemSettings');
      if (storedSettings) {
        const parsedSettings = JSON.parse(storedSettings);
        if (parsedSettings.language) setCurrentLanguage(parsedSettings.language);
        if (parsedSettings.printerStatus) setPrinterStatus(parsedSettings.printerStatus);
        if (parsedSettings.paymentMethods) setPaymentMethods(parsedSettings.paymentMethods);
        if (parsedSettings.billingInfo) setBillingInfo(parsedSettings.billingInfo);
      }
    } catch (error) {
      console.error('Error loading settings from localStorage:', error);
    }
  };
  
  // Load settings from API with localStorage fallback
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getSettings();
        const language = settings.language || 'English';
        setCurrentLanguage(language);
        changeLanguage(language);
        setPrinterStatus(settings.printerStatus || 'Not configured');
        setPaymentMethods(settings.paymentMethods || []);
        setBillingInfo(prev => settings.billingInfo || prev);
      } catch (error) {
        console.error('Error fetching settings from API:', error);
        loadFromLocalStorage();
      }
    };
    
    loadSettings();
  }, []);
  
  // Save settings both to API and localStorage
  const saveSettings = async (updatedSettings) => {
    try {
      await updateSettings({
        theme: updatedSettings.theme || (isDarkMode ? 'dark' : 'light'),
        language: updatedSettings.language || currentLanguage,
        printerStatus: updatedSettings.printerStatus || printerStatus,
        paymentMethods: updatedSettings.paymentMethods || paymentMethods,
        billingInfo: updatedSettings.billingInfo || billingInfo
      });
      
      // Also update localStorage as fallback
      localStorage.setItem('posSystemSettings', JSON.stringify({
        theme: updatedSettings.theme || (isDarkMode ? 'dark' : 'light'),
        language: updatedSettings.language || currentLanguage,
        printerStatus: updatedSettings.printerStatus || printerStatus,
        paymentMethods: updatedSettings.paymentMethods || paymentMethods,
        billingInfo: updatedSettings.billingInfo || billingInfo
      }));
    } catch (error) {
      console.error('Error saving settings to API:', error);
      // Save to localStorage as fallback
      localStorage.setItem('posSystemSettings', JSON.stringify({
        theme: updatedSettings.theme || (isDarkMode ? 'dark' : 'light'),
        language: updatedSettings.language || currentLanguage,
        printerStatus: updatedSettings.printerStatus || printerStatus,
        paymentMethods: updatedSettings.paymentMethods || paymentMethods,
        billingInfo: updatedSettings.billingInfo || billingInfo
      }));
    }
  };
  
  // Handle appearance settings save
  const handleAppearanceSave = (settings) => {
    setCurrentLanguage(settings.language);
    changeLanguage(settings.language);
    
    const shouldBeDark = settings.theme === 'dark';
    if (shouldBeDark !== isDarkMode) {
      toggleThemeMode();
    }
    
    saveSettings({ 
      language: settings.language,
      theme: settings.theme 
    });
    setOpenAppearanceDialog(false);
  };
  
  // Handle printer settings save
  const handlePrinterSave = (status) => {
    setPrinterStatus(status);
    saveSettings({ printerStatus: status });
    setOpenPrinterDialog(false);
  };
  
  // Handle payment settings save
  const handlePaymentSave = (methods) => {
    setPaymentMethods(methods);
    saveSettings({ paymentMethods: methods });
    setOpenPaymentDialog(false);
  };
  
  // Handle billing settings save
  const handleBillingSave = (updatedBillingInfo) => {
    setBillingInfo(updatedBillingInfo);
    saveSettings({ billingInfo: updatedBillingInfo });
    setOpenBillingDialog(false);
  };
  
  // Quick toggle dark/light mode
  const handleThemeToggle = () => {
    toggleThemeMode();
    saveSettings({ theme: !isDarkMode ? 'dark' : 'light' });
  };
  
  // Helper function to get billing display info
  const getBillingDisplay = () => {
    if (billingInfo.companyName) {
      return {
        company: billingInfo.companyName,
        plan: billingInfo.subscription?.plan || 'basic',
        status: billingInfo.subscription?.status || 'active'
      };
    }
    return {
      company: t('status.notConfigured'),
      plan: 'basic',
      status: 'not_configured'
    };
  };

  const billingDisplay = getBillingDisplay();
  
  return (
    <Box sx={{ pb: 6 }}>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('settings.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('settings.subtitle')}
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          {/* Appearance Section - Check Permission */}
          {hasPermission('/dashboard/settings/appearance') && (
            <Grid item xs={12} md={6} lg={4}>
              <SettingsSection
                icon={<AppearanceIcon />}
                title={t('settings.appearance')}
                subtitle={t('settings.appearanceSubtitle')}
                content={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle2">{t('settings.displayMode')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {isDarkMode ? t('settings.darkMode') : t('settings.lightMode')}
                        </Typography>
                      </Box>
                      <IconButton 
                        onClick={handleThemeToggle}
                        sx={{ 
                          backgroundColor: theme.palette.action.hover,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                          }
                        }}
                      >
                        {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                      </IconButton>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle2">{t('settings.language')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {currentLanguage}
                        </Typography>
                      </Box>
                      <IconButton 
                        onClick={() => setOpenAppearanceDialog(true)}
                        sx={{ 
                          backgroundColor: theme.palette.action.hover,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                          }
                        }}
                      >
                        <LanguageIcon />
                      </IconButton>
                    </Box>
                  </Box>
                }
                onConfigureClick={() => setOpenAppearanceDialog(true)}
              />
            </Grid>
          )}
          
          {/* Printer Section - Check Permission */}
          {hasPermission('/dashboard/settings/printer') && (
            <Grid item xs={12} md={6} lg={4}>
              <SettingsSection
                icon={<PrinterIcon />}
                title={t('settings.printer')}
                subtitle={t('settings.printerSubtitle')}
                content={
                  <Box>
                    <Typography variant="subtitle2">{t('settings.printerStatus')}</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: printerStatus === 'Connected' 
                          ? 'success.main' 
                          : 'text.secondary' 
                      }}
                    >
                      {printerStatus}
                    </Typography>
                  </Box>
                }
                onConfigureClick={() => setOpenPrinterDialog(true)}
              />
            </Grid>
          )}
          
          {/* Payment Section - Check Permission */}
          {hasPermission('/dashboard/settings/payment') && (
            <Grid item xs={12} md={6} lg={4}>
              <SettingsSection
                icon={<PaymentIcon />}
                title={t('settings.payment')}
                subtitle={t('settings.paymentSubtitle')}
                content={
                  <Box>
                    <Typography variant="subtitle2">{t('settings.paymentMethods')}</Typography>
                    {paymentMethods.length > 0 ? (
                      paymentMethods.map((method, index) => (
                        <Typography key={index} variant="body2" color="text.secondary">
                          • {method}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('status.notConfigured')}
                      </Typography>
                    )}
                  </Box>
                }
                onConfigureClick={() => setOpenPaymentDialog(true)}
              />
            </Grid>
          )}
          
          {/* Notification Settings - Check Permission - UPDATED */}
          {hasPermission('/dashboard/settings/notifications') && (
            <Grid item xs={12} md={6} lg={4}>
              <SettingsSection
                icon={<NotificationsIcon />}
                title={t('settings.notifications')}
                subtitle={t('settings.notificationsSubtitle')}
                content={
                  <Box>
                    <Typography variant="subtitle2">{t('notifications.preferences')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('notifications.configureDeliveryPreferences')}
                    </Typography>
                  </Box>
                }
                onConfigureClick={() => setOpenNotificationDialog(true)}
              />
            </Grid>
          )}
          
          {/* Billing Section - Check Permission */}
          {hasPermission('/dashboard/settings/billing') && (
            <Grid item xs={12} md={6} lg={4}>
              <SettingsSection
                icon={<BillingIcon />}
                title={t('billing.title')}
                subtitle={t('billing.subtitle')}
                content={
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">{t('billing.fields.companyName')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {billingDisplay.company}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">{t('billing.fields.currentPlan')}</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: billingDisplay.status === 'active' ? 'success.main' : 'text.secondary',
                          textTransform: 'capitalize'
                        }}
                      >
                        {billingDisplay.plan} - {billingDisplay.status}
                      </Typography>
                    </Box>

                    {billingInfo.paymentMethods && billingInfo.paymentMethods.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2">{t('billing.sections.paymentMethods')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {billingInfo.paymentMethods.length} {t('billing.methodsConfigured')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
                onConfigureClick={() => setOpenBillingDialog(true)}
              />
            </Grid>
          )}
          
          {/* Role Permissions - Check Permission (Admin only) */}
          {hasPermission('/dashboard/settings/roles') && (
            <Grid item xs={12} md={6} lg={4}>
              <SettingsSection
                icon={<SecurityIcon />}
                title={t('settings.rolePermissions')}
                subtitle={t('settings.roleSubtitle')}
                content={
                  <Box>
                    <Typography variant="subtitle2">Role Management</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('settings.roleSubtitle')}
                    </Typography>
                  </Box>
                }
                onConfigureClick={() => setOpenRoleDialog(true)}
              />
            </Grid>
          )}
        </Grid>
      </MotionDiv>
      
      {/* Dialogs - Only render if user has permission */}
      {hasPermission('/dashboard/settings/appearance') && (
        <AppearanceDialog
          open={openAppearanceDialog}
          onClose={() => setOpenAppearanceDialog(false)}
          currentSettings={{ language: currentLanguage }}
          onSave={handleAppearanceSave}
        />
      )}
      
      {hasPermission('/dashboard/settings/printer') && (
        <PrinterDialog
          open={openPrinterDialog}
          onClose={() => setOpenPrinterDialog(false)}
          currentStatus={printerStatus}
          onSave={handlePrinterSave}
        />
      )}
      
      {hasPermission('/dashboard/settings/payment') && (
        <PaymentDialog
          open={openPaymentDialog}
          onClose={() => setOpenPaymentDialog(false)}
          currentMethods={paymentMethods}
          onSave={handlePaymentSave}
        />
      )}
      
      {hasPermission('/dashboard/settings/billing') && (
        <BillingDialog
          open={openBillingDialog}
          onClose={() => setOpenBillingDialog(false)}
          currentBillingInfo={billingInfo}
          onSave={handleBillingSave}
        />
      )}
      
      {/* NEW: Notification Settings Dialog */}
      {hasPermission('/dashboard/settings/notifications') && (
        <NotificationSettingsDialog
          open={openNotificationDialog}
          onClose={() => setOpenNotificationDialog(false)}
        />
      )}
      
      {hasPermission('/dashboard/settings/roles') && (
        <RolePermissionsDialog
          open={openRoleDialog}
          onClose={() => setOpenRoleDialog(false)}
        />
      )}
    </Box>
  );
};

export default Settings;